import { useMemo } from 'react';

/**
 * Hook for calculating summary statistics from grid row data.
 * Useful for displaying summary cards above a grid.
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.rowData - Array of row objects
 * @param {Object} options.calculations - Calculation definitions { key: { label, calculate, color, format, icon } }
 * @returns {Object} Calculated statistics
 *
 * @example
 * const { stats } = useSummaryStats({
 *   rowData: comparisonData,
 *   calculations: {
 *     totalSecurities: {
 *       label: 'Total Securities',
 *       calculate: (data) => data.length,
 *       color: 'primary',
 *     },
 *     priceChanges: {
 *       label: 'Price Changes',
 *       calculate: countWhere(row => row.STATUS?.includes('Price')),
 *       color: 'warning',
 *     },
 *     avgChange: {
 *       label: 'Avg Change %',
 *       calculate: averageField('PRICE_CHANGE_PERCENT'),
 *       color: 'secondary',
 *       format: (val) => `${val.toFixed(2)}%`,
 *     },
 *   },
 * });
 *
 * // stats = [
 * //   { key: 'totalSecurities', label: 'Total Securities', value: 150, color: 'primary' },
 * //   { key: 'priceChanges', label: 'Price Changes', value: 45, color: 'warning' },
 * //   { key: 'avgChange', label: 'Avg Change %', value: '2.35%', color: 'secondary' },
 * // ]
 */
export const useSummaryStats = ({ rowData = [], calculations = {} }) => {
  const stats = useMemo(() => {
    if (!rowData || rowData.length === 0) {
      // Return stats with zero/default values
      return Object.entries(calculations).map(([key, config]) => ({
        key,
        label: config.label || key,
        value: config.format ? config.format(0) : 0,
        rawValue: 0,
        color: config.color || 'primary',
        icon: config.icon,
      }));
    }

    return Object.entries(calculations).map(([key, config]) => {
      const { label, calculate, color = 'primary', format, icon } = config;

      // Execute the calculation
      let rawValue = 0;
      try {
        rawValue = typeof calculate === 'function' ? calculate(rowData) : 0;
      } catch (error) {
        console.warn(`Error calculating stat "${key}":`, error);
        rawValue = 0;
      }

      // Format the display value
      const displayValue = format ? format(rawValue) : rawValue;

      return {
        key,
        label: label || key,
        value: displayValue,
        rawValue,
        color,
        icon,
      };
    });
  }, [rowData, calculations]);

  // Also return as an object keyed by stat name for easier access
  const statsMap = useMemo(() => {
    return stats.reduce((acc, stat) => {
      acc[stat.key] = stat;
      return acc;
    }, {});
  }, [stats]);

  return {
    /** Array of calculated statistics */
    stats,
    /** Object mapping stat keys to stat objects */
    statsMap,
    /** Total row count for convenience */
    rowCount: rowData?.length || 0,
  };
};

// ==========================================
// Helper calculation functions
// ==========================================

/**
 * Creates a count function that counts rows matching a predicate.
 *
 * @param {Function} predicate - Function that returns true for rows to count: (row) => boolean
 * @returns {Function} Calculation function for useSummaryStats
 *
 * @example
 * countWhere(row => row.STATUS === 'Price Increased')
 * countWhere(row => row.PRICE > 100)
 */
export const countWhere = (predicate) => {
  return (data) => data.filter(predicate).length;
};

/**
 * Creates a sum function for a specific field.
 *
 * @param {string} field - Field name to sum
 * @returns {Function} Calculation function for useSummaryStats
 *
 * @example
 * sumField('QUANTITY')
 * sumField('MARKET_VALUE')
 */
export const sumField = (field) => {
  return (data) =>
    data.reduce((sum, row) => {
      const value = parseFloat(row[field]);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
};

/**
 * Creates an average function for a specific field.
 *
 * @param {string} field - Field name to average
 * @param {boolean} [ignoreNaN=true] - Whether to ignore NaN values in the average
 * @returns {Function} Calculation function for useSummaryStats
 *
 * @example
 * averageField('PRICE_CHANGE_PERCENT')
 * averageField('DURATION', false)
 */
export const averageField = (field, ignoreNaN = true) => {
  return (data) => {
    let sum = 0;
    let count = 0;

    data.forEach((row) => {
      const value = parseFloat(row[field]);
      if (!isNaN(value)) {
        sum += value;
        count++;
      } else if (!ignoreNaN) {
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };
};

/**
 * Creates a min function for a specific field.
 *
 * @param {string} field - Field name to find minimum
 * @returns {Function} Calculation function for useSummaryStats
 */
export const minField = (field) => {
  return (data) => {
    let min = Infinity;
    data.forEach((row) => {
      const value = parseFloat(row[field]);
      if (!isNaN(value) && value < min) {
        min = value;
      }
    });
    return min === Infinity ? 0 : min;
  };
};

/**
 * Creates a max function for a specific field.
 *
 * @param {string} field - Field name to find maximum
 * @returns {Function} Calculation function for useSummaryStats
 */
export const maxField = (field) => {
  return (data) => {
    let max = -Infinity;
    data.forEach((row) => {
      const value = parseFloat(row[field]);
      if (!isNaN(value) && value > max) {
        max = value;
      }
    });
    return max === -Infinity ? 0 : max;
  };
};

/**
 * Creates a count function for unique values in a field.
 *
 * @param {string} field - Field name to count unique values
 * @returns {Function} Calculation function for useSummaryStats
 */
export const countUnique = (field) => {
  return (data) => {
    const uniqueValues = new Set();
    data.forEach((row) => {
      const value = row[field];
      if (value !== null && value !== undefined && value !== '') {
        uniqueValues.add(value);
      }
    });
    return uniqueValues.size;
  };
};

/**
 * Creates a function that returns a specific percentile of a field.
 *
 * @param {string} field - Field name
 * @param {number} percentile - Percentile to calculate (0-100)
 * @returns {Function} Calculation function for useSummaryStats
 */
export const percentileField = (field, percentile) => {
  return (data) => {
    const values = data
      .map((row) => parseFloat(row[field]))
      .filter((v) => !isNaN(v))
      .sort((a, b) => a - b);

    if (values.length === 0) return 0;

    const index = Math.ceil((percentile / 100) * values.length) - 1;
    return values[Math.max(0, index)];
  };
};

/**
 * Creates a function that calculates the standard deviation of a field.
 *
 * @param {string} field - Field name
 * @returns {Function} Calculation function for useSummaryStats
 */
export const stdDevField = (field) => {
  return (data) => {
    const values = data.map((row) => parseFloat(row[field])).filter((v) => !isNaN(v));

    if (values.length === 0) return 0;

    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
    const variance = squaredDiffs.reduce((sum, v) => sum + v, 0) / values.length;

    return Math.sqrt(variance);
  };
};

// ==========================================
// Common format functions
// ==========================================

/**
 * Formats a number with commas and optional decimal places.
 *
 * @param {number} decimals - Number of decimal places
 * @returns {Function} Format function
 */
export const formatNumber = (decimals = 0) => {
  return (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
};

/**
 * Formats a number as currency.
 *
 * @param {string} currency - Currency code (default: 'USD')
 * @param {number} decimals - Number of decimal places
 * @returns {Function} Format function
 */
export const formatCurrency = (currency = 'USD', decimals = 2) => {
  return (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '$0';
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
};

/**
 * Formats a number as a percentage.
 *
 * @param {number} decimals - Number of decimal places
 * @param {boolean} includeSign - Include + sign for positive numbers
 * @returns {Function} Format function
 */
export const formatPercent = (decimals = 2, includeSign = false) => {
  return (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0%';
    const formatted = value.toFixed(decimals);
    if (includeSign && value > 0) {
      return `+${formatted}%`;
    }
    return `${formatted}%`;
  };
};

export default useSummaryStats;
